import React, { useState } from 'react';
import { Check, LayoutGrid, Lock } from 'lucide-react';
import Modal from '../../../shared/components/ui/Modal';

const ICON_COLUMN_CLASS = 'flex w-12 shrink-0 flex-col items-center';
const SUBSTEP_PREVIEW_LIMIT = 5;

const sortSubStepsForDisplay = (subSteps) => {
  const rank = { active: 0, available: 1, locked: 2, completed: 3 };
  return [...subSteps].sort(
    (a, b) => (rank[a.status] ?? 9) - (rank[b.status] ?? 9)
  );
};

const StageIconBadge = ({ stage }) => {
  if (stage.status === 'completed') {
    return (
      <div className="mt-1.5 flex h-9 w-9 items-center justify-center rounded-full border-2 border-emerald-500 bg-emerald-50 text-emerald-600">
        <Check className="h-4 w-4" strokeWidth={2.5} />
      </div>
    );
  }

  if (stage.status === 'active') {
    return (
      <div className="flex h-12 w-12 items-center justify-center rounded-full border-[3px] border-[#2A565E] bg-[#2A565E] text-white shadow-md">
        <LayoutGrid className="h-5 w-5" strokeWidth={1.75} />
      </div>
    );
  }

  return (
    <div className="mt-1.5 flex h-9 w-9 items-center justify-center rounded-full border-2 border-gray-300 bg-gray-50 text-gray-400">
      <Lock className="h-3.5 w-3.5" strokeWidth={2} />
    </div>
  );
};

const getActiveMissionLabel = (phase) => {
  if (phase === 'available') return 'In progress';
  if (phase === 'delayed') return 'Delayed mission';
  return 'Current mission';
};

const SubStepRow = ({ subStep, onSubStepAction }) => {
  const isCompleted = subStep.status === 'completed';
  const isActive = subStep.status === 'active';
  const isLocked = subStep.status === 'locked';
  const actionLabel =
    subStep.actionLabel || (subStep.vertical ? 'Create user' : null);
  const isActionable =
    !isCompleted && !isLocked && Boolean(onSubStepAction) && Boolean(actionLabel);

  return (
    <div
      role={isActionable ? 'button' : undefined}
      tabIndex={isActionable ? 0 : undefined}
      onClick={() => isActionable && onSubStepAction?.(subStep)}
      onKeyDown={(event) => {
        if (isActionable && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onSubStepAction?.(subStep);
        }
      }}
      className={`flex items-start justify-between gap-3 rounded-lg border px-3 py-2.5 ${
        isCompleted
          ? 'border-emerald-200 bg-emerald-50/50'
          : isActive
            ? 'border-[#2A565E]/40 bg-[#2A565E]/5'
            : isLocked
              ? 'border-gray-200 bg-gray-50'
              : 'border-gray-200 bg-white'
      } ${isActionable ? 'cursor-pointer hover:border-[#2A565E]/30 hover:bg-[#2A565E]/5' : ''}`}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {subStep.vertical && (
            <span className="rounded bg-[#2A565E]/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#2A565E]">
              {subStep.vertical}
            </span>
          )}
          {isActive && (
            <span className="inline-flex items-center rounded bg-[#2A565E]/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#2A565E]">
              Active
            </span>
          )}
          {isCompleted && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-600">
              <Check className="h-3 w-3" strokeWidth={2.5} />
              Done
            </span>
          )}
        </div>
        <p
          className={`mt-1 text-xs font-semibold leading-snug ${
            isCompleted ? 'text-gray-500' : 'text-[#0f2a45]'
          }`}>
          {subStep.title}
        </p>
        {subStep.owner && (
          <p className="mt-0.5 text-[11px] text-gray-500">Owner: {subStep.owner}</p>
        )}
      </div>
      {isActionable && actionLabel && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onSubStepAction?.(subStep);
          }}
          className="shrink-0 rounded-lg border border-[#2A565E] bg-white px-3 py-1.5 text-[11px] font-semibold text-[#2A565E] transition-colors hover:bg-[#2A565E]/5">
          {actionLabel}
        </button>
      )}
    </div>
  );
};

const SubStepList = ({
  subSteps,
  onSubStepAction,
  stageTitle = null,
  hideCompleted = false,
  className = 'mt-4 space-y-2'
}) => {
  const [isAllOpen, setIsAllOpen] = useState(false);

  if (!subSteps?.length) return null;

  const completedCount = subSteps.filter((subStep) => subStep.status === 'completed').length;
  const displaySubSteps = hideCompleted
    ? subSteps.filter((subStep) => subStep.status !== 'completed')
    : subSteps;

  if (!displaySubSteps.length) return null;

  const totalCount = displaySubSteps.length;
  const orderedSubSteps = sortSubStepsForDisplay(displaySubSteps);
  const hasMore = totalCount > SUBSTEP_PREVIEW_LIMIT;
  const visibleSubSteps = hasMore
    ? orderedSubSteps.slice(0, SUBSTEP_PREVIEW_LIMIT)
    : orderedSubSteps;

  const renderSubStepRows = (items) =>
    items.map((subStep) => (
      <SubStepRow
        key={subStep.id}
        subStep={subStep}
        onSubStepAction={onSubStepAction}
      />
    ));

  return (
    <>
      <div className={className}>
        {renderSubStepRows(visibleSubSteps)}
        {hasMore && (
          <button
            type="button"
            onClick={() => setIsAllOpen(true)}
            className="mt-2 w-full rounded-lg border border-dashed border-gray-300 bg-gray-50/80 px-3 py-2.5 text-xs font-semibold text-[#2A565E] transition-colors hover:border-[#2A565E]/40 hover:bg-[#2A565E]/5">
            View all ({totalCount})
          </button>
        )}
      </div>

      {isAllOpen && (
        <Modal
          name={
            stageTitle
              ? hideCompleted
                ? `${stageTitle} — remaining tasks`
                : `${stageTitle} — all tasks`
              : 'All subtasks'
          }
          width={48}
          onClose={() => setIsAllOpen(false)}>
          <div className="px-6 pb-6">
            <p className="mb-4 text-sm text-gray-500">
              {completedCount}/{subSteps.length} completed
            </p>
            <div className="max-h-[min(70vh,32rem)] space-y-2 overflow-y-auto pr-1">
              {renderSubStepRows(orderedSubSteps)}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

const StageContent = ({ stage, onAction, onSubStepAction }) => {
  if (stage.status === 'active') {
    const missionLabel = getActiveMissionLabel(stage.phase);

    return (
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#2A565E]">
          {missionLabel}
        </p>
        <p className="mt-0.5 text-base font-semibold text-[#0f2a45]">{stage.title}</p>
        {stage.description && <p className="mt-1 text-xs text-gray-500">{stage.description}</p>}
        {stage.totalSubSteps != null && stage.totalSubSteps > 0 && (
          <p className="mt-1 text-xs font-medium text-[#2A565E]">
            {stage.completedSubSteps ?? 0}/{stage.totalSubSteps} complete
          </p>
        )}
        {stage.owner && (
          <p className="mt-1 text-xs text-gray-500">
            Owner: {stage.owner}
            {stage.pendingDays != null ? ` – ${stage.pendingDays} days pending` : ''}
          </p>
        )}
        {stage.subSteps?.length > 0 && (
          <SubStepList
            subSteps={stage.subSteps}
            onSubStepAction={onSubStepAction}
            stageTitle={stage.title}
            hideCompleted
          />
        )}
        {stage.actions?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {stage.actions.map((action) => (
              <button
                key={action}
                type="button"
                onClick={() => onAction?.(stage)}
                className="rounded-lg border border-[#2A565E] bg-white px-4 py-1.5 text-xs font-semibold text-[#2A565E] transition-colors hover:bg-[#2A565E]/5">
                {action}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  const isCompleted = stage.status === 'completed';
  const textTone = isCompleted ? 'text-gray-500' : 'text-gray-400';

  return (
    <div className="pt-1.5">
      <p
        className={`text-sm font-semibold leading-snug ${
          isCompleted ? 'text-[#0f2a45]' : 'text-gray-400'
        }`}>
        {stage.title}
      </p>
      {stage.description && (
        <p className={`mt-0.5 text-xs leading-snug ${textTone}`}>{stage.description}</p>
      )}
      {stage.needsLabel && (
        <p className={`mt-0.5 text-xs leading-snug ${textTone}`}>{stage.needsLabel}</p>
      )}
      {!stage.description && stage.subtitle && (
        <p className={`mt-0.5 text-xs leading-snug ${textTone}`}>{stage.subtitle}</p>
      )}
      {isCompleted && (
        <SubStepList
          subSteps={stage.subSteps}
          onSubStepAction={onSubStepAction}
          stageTitle={stage.title}
          className="mt-3 space-y-2"
        />
      )}
    </div>
  );
};

export const StageRow = ({ stage, isLast, onAction, onSubStepAction }) => (
  <div className="flex gap-4">
    <div className={ICON_COLUMN_CLASS}>
      <StageIconBadge stage={stage} />
      {!isLast && <div className="my-1.5 w-px flex-1 min-h-[32px] bg-gray-200" />}
    </div>
    <div className="min-w-0 flex-1 pb-6">
      <StageContent stage={stage} onAction={onAction} onSubStepAction={onSubStepAction} />
    </div>
  </div>
);

export const ProgressionTimelineBody = ({ stages, onAction, onSubStepAction }) => {
  const splitAt = stages.length > 5 ? Math.ceil(stages.length / 2) : stages.length;
  const leftStages = stages.slice(0, splitAt);
  const rightStages = stages.length > 5 ? stages.slice(splitAt) : [];

  return (
    <div
      className={`grid grid-cols-1 gap-8 ${
        rightStages.length > 0 ? 'lg:grid-cols-2 lg:gap-12' : ''
      }`}>
      <div className="flex flex-col">
        {leftStages.map((stage, index) => (
          <StageRow
            key={stage.id}
            stage={stage}
            isLast={index === leftStages.length - 1}
            onAction={onAction}
            onSubStepAction={onSubStepAction}
          />
        ))}
      </div>
      {rightStages.length > 0 && (
        <div className="flex flex-col lg:pt-[88px]">
          {rightStages.map((stage, index) => (
            <StageRow
              key={stage.id}
              stage={stage}
              isLast={index === rightStages.length - 1}
              onAction={onAction}
              onSubStepAction={onSubStepAction}
            />
          ))}
        </div>
      )}
    </div>
  );
};
